// The one place coin constants and price formatting live. The backend (backend/money) imports this file;
// never format a coin price anywhere else, and never hard-code the coin's name.
//
// Coins are priced per country (docs/build/pricing.md). Money computes each country's pack prices and hands the
// viewer a `LocalPrice` (GET /v1/me/pricing → `local`). With one, `formatPrice(coins, local)` shows the viewer's own
// price, `500 ◆ (≈ €5.39)`. Without one (country unknown: signed out, or never checked out) it falls back to the old
// USD form, `500 ◆ (≈ $5.00)`, at 100 coins ≈ $1.00.

/** Working name, not final (docs/PLAN.md open decision 5). Change it here only. */
export const COIN_NAME = "vibecoins";
export const COIN_SYMBOL = "◆";
/** Fallback rate, used only when the viewer's country is unknown: 100 coins ≈ this many US cents. */
export const CENTS_PER_100_COINS = 100;

/**
 * The viewer's own coin price, from money (`GET /v1/me/pricing` → `local`). The formatting data travels with it, so no
 * client needs a currency table.
 *
 * The `≈` figure (docs/build/safety.md "The ≈ figure"): with `ladder`, coins are valued along money's ladder, the lower
 * envelope of what that many coins really cost there (the exact top-up price for that amount). It is never more than
 * that price, exact for the smallest pack, and a few percent under elsewhere (bigger packs cost less per coin, so the
 * smallest pack's rate overstated large amounts by up to ~14%). Without `ladder` (an older money), the smallest pack's
 * rate, `coins × pack_minor / pack_coins`.
 */
export interface LocalPrice {
  /** The country the price is for (ISO 3166-1 alpha-2), or "ZZ" for the rest-of-world default. */
  country: string;
  /** ISO 4217 code, e.g. "EUR", "HUF". */
  currency: string;
  /** "€", "$", "£", "Ft", "kr", "CHF", … */
  symbol: string;
  /** true: `€5.39`, `CHF 5.90`; false: `2,090 Ft`. */
  symbol_first: boolean;
  /** Digits after the point in prices: 2 (EUR, USD…) or 0 (HUF, SEK…: whole units). */
  decimals: number;
  /** The smallest pack: its coins and its exact price in minor units (`decimals` digits). */
  pack_coins: number;
  pack_minor: number;
  /**
   * `[coins, minor]` breakpoints, coins ascending, starting at the smallest pack: the estimate is linear between them,
   * the first point's rate below it and the last point's rate beyond it. Optional (older money).
   */
  ladder?: [number, number][];
}

/** Fallback value in US cents of `coins` (rounded half up). Used only when the viewer's country is unknown. */
export function coinsToCents(coins: number): number {
  assertCoins(coins);
  return Math.round((coins * CENTS_PER_100_COINS) / 100);
}

/** Thousands separators without Intl, so output is identical in every runtime. */
function group(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const USD_FORMAT = { symbol: "$", symbol_first: true, decimals: 2 };

/** An amount in minor units: `€5.39`, `$4.69`, `2,090 Ft`, `CHF 5.90`, `59 kr`. */
export function formatMoney(minor: number, f: Pick<LocalPrice, "symbol" | "symbol_first" | "decimals">): string {
  if (!Number.isSafeInteger(minor)) throw new RangeError(`amount must be an integer, got ${minor}`);
  const sign = minor < 0 ? "-" : "";
  const abs = Math.abs(minor);
  const unit = 10 ** f.decimals;
  const num = f.decimals > 0 ? `${group(Math.floor(abs / unit))}.${String(abs % unit).padStart(f.decimals, "0")}` : group(abs);
  if (!f.symbol_first) return `${sign}${num} ${f.symbol}`;
  // Letter symbols ("CHF") take a space, sign symbols ("€", "$") don't.
  return `${sign}${f.symbol}${/[A-Za-z]$/.test(f.symbol) ? " " : ""}${num}`;
}

/** `12345` cents → `$123.45`. */
export function formatCents(cents: number): string {
  return formatMoney(cents, USD_FORMAT);
}

/**
 * Approximate local value of `coins` in minor units: along `local.ladder` when there is one (rounded down, so never
 * above the real price), else at the smallest pack's rate (rounded half up).
 */
export function coinsToLocal(coins: number, local: LocalPrice): number {
  assertCoins(coins);
  const lad = validLadder(local.ladder);
  if (lad) {
    const [c0, p0] = lad[0];
    if (coins <= c0) return Math.floor((coins * p0) / c0);
    for (let i = 1; i < lad.length; i++) {
      const [b, pb] = lad[i];
      if (coins <= b) {
        const [a, pa] = lad[i - 1];
        return pa + Math.floor(((coins - a) * (pb - pa)) / (b - a));
      }
    }
    const [b, pb] = lad[lad.length - 1];
    return Math.floor((coins * pb) / b); // beyond the largest top-up: its per-coin rate
  }
  if (!Number.isSafeInteger(local.pack_coins) || local.pack_coins <= 0) throw new RangeError("LocalPrice.pack_coins must be a positive integer");
  return Math.floor((2 * coins * local.pack_minor + local.pack_coins) / (2 * local.pack_coins));
}

/** The ladder if it is usable (positive integers, coins strictly ascending, prices not falling), else null. */
function validLadder(l: LocalPrice["ladder"]): [number, number][] | null {
  if (!Array.isArray(l) || l.length === 0) return null;
  for (let i = 0; i < l.length; i++) {
    const p = l[i];
    if (!Array.isArray(p) || !Number.isSafeInteger(p[0]) || !Number.isSafeInteger(p[1]) || p[0] <= 0 || p[1] < 0) return null;
    if (i > 0 && (p[0] <= l[i - 1][0] || p[1] < l[i - 1][1])) return null;
  }
  return l;
}

/**
 * Every coin price is shown this way: `500 ◆ (≈ €5.39)` in the viewer's currency when `local` is known, else the
 * fallback `500 ◆ (≈ $5.00)`.
 */
export function formatPrice(coins: number, local?: LocalPrice | null): string {
  const approx = local ? formatMoney(coinsToLocal(coins, local), local) : formatCents(coinsToCents(coins));
  return `${group(coins)} ${COIN_SYMBOL} (≈ ${approx})`;
}

function assertCoins(coins: number): void {
  if (!Number.isSafeInteger(coins) || coins < 0) throw new RangeError(`coins must be a non-negative integer, got ${coins}`);
}
